import { firestore } from '../plugins/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth } from '../plugins/firebase';

/**
 * Activity Logger Service
 * Protokolliert alle wichtigen Benutzeraktionen in der Firestore "activities" Collection
 */

export const ActivityType = {
  // Benutzer-Management
  USER_CREATED: 'user_created',
  USER_DELETED: 'user_deleted',
  USER_UPDATED: 'user_updated',
  USER_PERMISSION_CHANGED: 'user_permission_changed',
  USER_PASSWORD_RESET: 'user_password_reset',
  USER_TOOL_ACCESS_ADDED: 'user_tool_access_added',
  USER_TOOL_ACCESS_REMOVED: 'user_tool_access_removed',
  USER_CLAIM_CHANGED: 'user_claim_changed',
  USER_DEACTIVATION_SCHEDULED: 'user_deactivation_scheduled',
  USER_DEACTIVATION_CANCELLED: 'user_deactivation_cancelled',
  USER_REACTIVATED: 'user_reactivated',
  USER_DEACTIVATED_AUTOMATIC: 'user_deactivated_automatic',
  
  // Tool/App-Management
  TOOL_CREATED: 'tool_created',
  TOOL_UPDATED: 'tool_updated',
  TOOL_DELETED: 'tool_deleted',
  TOOL_ICON_UPLOADED: 'tool_icon_uploaded',
  TOOL_CLAIMS_CHANGED: 'tool_claims_changed',
  TOOL_MAINTENANCE_CHANGED: 'tool_maintenance_changed',
  TOOL_MAINTENANCE_SESSIONS_REVOKED: 'tool_maintenance_sessions_revoked',
  
  // Authentifizierung
  LOGIN: 'login',
  LOGOUT: 'logout',
};

/**
 * Protokolliert eine Aktivität in Firestore
 * @param {string} actionType - Typ der Aktion (aus ActivityType)
 * @param {Object} details - Details zur Aktion
 * @param {string} details.targetUser - UID oder Name des betroffenen Benutzers (optional)
 * @param {string} details.targetTool - ID oder Name des betroffenen Tools (optional)
 * @param {Object} details.before - Zustand vor der Änderung (optional)
 * @param {Object} details.after - Zustand nach der Änderung (optional)
 * @param {Object} details.metadata - Zusätzliche Metadaten (optional)
 */
export async function logActivity(actionType, details = {}) {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.warn('Keine Aktivität geloggt: Kein authentifizierter Benutzer');
      return;
    }

    const activityData = {
      actionType,
      actorUid: currentUser.uid,
      actorEmail: currentUser.email || 'unknown',
      actorDisplayName: currentUser.displayName || currentUser.email || 'Unknown User',
      timestamp: serverTimestamp(),
      ...details
    };

    await addDoc(collection(firestore, 'activities'), activityData);
    
  } catch (error) {
    console.error('Fehler beim Loggen der Aktivität:', error);
    // Fehler nicht weiterwerfen, um den Hauptprozess nicht zu unterbrechen
  }
}

/**
 * Hilfsfunktion zum Loggen von Benutzer-Berechtigungsänderungen
 */
export async function logPermissionChange(targetUserEmail, targetUserName, oldPermission, newPermission) {
  await logActivity(ActivityType.USER_PERMISSION_CHANGED, {
    targetUser: targetUserEmail,
    targetUserName: targetUserName,
    before: { permission: oldPermission },
    after: { permission: newPermission }
  });
}

/**
 * Hilfsfunktion zum Loggen von Tool-Zugriffänderungen
 */
export async function logToolAccessChange(targetUserEmail, targetUserName, toolId, toolName, accessGranted) {
  await logActivity(
    accessGranted ? ActivityType.USER_TOOL_ACCESS_ADDED : ActivityType.USER_TOOL_ACCESS_REMOVED,
    {
      targetUser: targetUserEmail,
      targetUserName: targetUserName,
      targetTool: toolName,
      toolId: toolId,
      accessGranted: accessGranted
    }
  );
}

/**
 * Hilfsfunktion zum Loggen von Tool-Änderungen
 */
export async function logToolChange(actionType, toolId, toolName, before = null, after = null, metadata = null) {
  const details = {
    targetTool: toolName,
    toolId: toolId
  };
  
  if (before) details.before = before;
  if (after) details.after = after;
  if (metadata) details.metadata = metadata;
  
  await logActivity(actionType, details);
}

/**
 * Hilfsfunktion zum Loggen von Benutzer-Änderungen
 */
export async function logUserChange(actionType, targetUserEmail, targetUserName, before = null, after = null) {
  const details = {
    targetUser: targetUserEmail,
    targetUserName: targetUserName
  };
  
  if (before) details.before = before;
  if (after) details.after = after;
  
  await logActivity(actionType, details);
}
